import { Injectable, inject } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { BehaviorSubject, filter } from 'rxjs'

export const EDITOR_MAXIMIZED_QUERY_PARAM = 'editor.max'

@Injectable({
  providedIn: 'root',
})
export class EditorSettingsService {
  private router = inject(Router)

  private showCitationsSubject = new BehaviorSubject<boolean>(true)
  showCitations$ = this.showCitationsSubject.asObservable()

  get showCitations(): boolean {
    return this.showCitationsSubject.value
  }

  setShowCitations(value: boolean) {
    this.showCitationsSubject.next(value)
  }

  private showWordCountSubject = new BehaviorSubject<boolean>(false)
  showWordCount$ = this.showWordCountSubject.asObservable()

  get showWordCount(): boolean {
    return this.showWordCountSubject.value
  }

  setShowWordCount(value: boolean) {
    this.showWordCountSubject.next(value)
  }

  private isEditorMaximizedSubject = new BehaviorSubject<boolean>(false)
  isEditorMaximized$ = this.isEditorMaximizedSubject.asObservable()

  constructor() {
    this.syncMaximizedFromUrl()
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => this.syncMaximizedFromUrl())
  }

  get isEditorMaximized(): boolean {
    return this.isEditorMaximizedSubject.value
  }

  setIsEditorMaximized(value: boolean) {
    if (this.isEditorMaximizedSubject.value === value) {
      return
    }
    this.isEditorMaximizedSubject.next(value)
    this.router
      .navigate([], {
        relativeTo: this.getDeepestActivatedRoute(),
        queryParams: { [EDITOR_MAXIMIZED_QUERY_PARAM]: value ? 't' : null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .then((success) => {
        if (!success) {
          this.syncMaximizedFromUrl()
        }
      })
      .catch(() => this.syncMaximizedFromUrl())
  }

  updateMaximizedStateOnly(value: boolean) {
    if (this.isEditorMaximizedSubject.value === value) {
      return
    }
    this.isEditorMaximizedSubject.next(value)
  }

  private syncMaximizedFromUrl() {
    const tree = this.router.parseUrl(this.router.url)
    const value = tree.queryParams[EDITOR_MAXIMIZED_QUERY_PARAM] === 't'
    if (this.isEditorMaximizedSubject.value !== value) {
      this.isEditorMaximizedSubject.next(value)
    }
  }

  private getDeepestActivatedRoute(): ActivatedRoute {
    const findLeaf = (route: ActivatedRoute): ActivatedRoute => (route.firstChild === null ? route : findLeaf(route.firstChild))
    return findLeaf(this.router.routerState.root)
  }
}
